if (!customElements.get('custom-product-carousel')) {
  // Same layout rules as the reference slider: centred, looping, slides per view and spacing per breakpoint.
  const BREAKPOINTS = [
    { query: '(min-width: 1000px)', perView: 4, spacing: 60 },
    { query: '(min-width: 500px)', perView: 2, spacing: 30 },
    { query: 'all', perView: 1.3, spacing: 10 },
  ];
  const DURATION = 500;
  const easeOutQuint = (t) => 1 + --t * t * t * t * t;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  customElements.define(
    'custom-product-carousel',
    class CustomProductCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-cx-track]');
        this.slides = Array.from(this.querySelectorAll('[data-cx-slide]'));
        if (!this.track || !this.slides.length) return;
        this.position = 0; // in slide units; 0 = first slide centred
        this.isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

        this.querySelector('[data-cx-prev]')?.addEventListener('click', () => this.go(-1));
        this.querySelector('[data-cx-next]')?.addEventListener('click', () => this.go(1));
        this.track.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.go(-1);
          if (event.key === 'ArrowRight') this.go(1);
        });
        if (!this.isTouch) {
          this.slides.forEach((slide) => {
            slide.addEventListener('mouseenter', () => slide.classList.add('is-hovered'));
            slide.addEventListener('mouseleave', () => slide.classList.remove('is-hovered'));
          });
        }
        this.initDrag();
        this.resizeObserver = new ResizeObserver(() => this.layout());
        this.resizeObserver.observe(this.track);
        this.layout();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.frame);
      }

      config() {
        return BREAKPOINTS.find((bp) => bp.query === 'all' || window.matchMedia(bp.query).matches);
      }

      layout() {
        const { perView, spacing } = this.config();
        this.size = this.track.getBoundingClientRect().width || 1;
        this.spacing = spacing;
        this.slideWidth = (this.size - spacing * (perView - 1)) / perView;
        this.step = this.slideWidth + spacing;
        // Loop as soon as the slides fill the track (keen-slider loops a 4-up row of 4 at desktop widths).
        this.loop = this.slides.length * this.step >= this.size;
        this.slides.forEach((slide) => {
          slide.style.minWidth = `${this.slideWidth}px`;
          slide.style.maxWidth = `${this.slideWidth}px`;
        });
        this.render();
      }

      render() {
        const count = this.slides.length;
        const total = count * this.step;
        const origin = this.size / 2 - this.slideWidth / 2;
        let natural = 0;
        this.slides.forEach((slide, index) => {
          let x = (index - this.position) * this.step;
          if (this.loop) x = ((((x + total / 2) % total) + total) % total) - total / 2;
          // Slides sit side by side in the flex track; translate from their natural spot to the target.
          slide.style.transform = `translate3d(${origin + x - natural}px, 0px, 0px)`;
          natural += this.slideWidth;
        });
        const active = ((Math.round(this.position) % count) + count) % count;
        this.slides.forEach((slide, index) => slide.classList.toggle('is-active', index === active));
      }

      go(delta) {
        const target = Math.round(this.position) + delta;
        this.animateTo(this.loop ? target : Math.max(0, Math.min(this.slides.length - 1, target)));
      }

      animateTo(target) {
        cancelAnimationFrame(this.frame);
        const start = this.position;
        const distance = target - start;
        if (reducedMotion() || distance === 0) {
          this.position = target;
          return this.render();
        }
        const began = performance.now();
        const tick = (now) => {
          const t = Math.min(1, (now - began) / DURATION);
          this.position = start + distance * easeOutQuint(t);
          this.render();
          if (t < 1) this.frame = requestAnimationFrame(tick);
        };
        this.frame = requestAnimationFrame(tick);
      }

      initDrag() {
        let startX = 0;
        let startPosition = 0;
        let lastX = 0;
        let lastTime = 0;
        let velocity = 0;
        let dragging = false;
        let moved = false;

        this.track.addEventListener('pointerdown', (event) => {
          if (event.button !== 0) return;
          dragging = true;
          moved = false;
          startX = lastX = event.clientX;
          lastTime = performance.now();
          startPosition = this.position;
          velocity = 0;
          cancelAnimationFrame(this.frame);
        });
        window.addEventListener('pointermove', (event) => {
          if (!dragging) return;
          const dx = event.clientX - startX;
          if (Math.abs(dx) > 5) moved = true;
          const now = performance.now();
          velocity = (event.clientX - lastX) / Math.max(1, now - lastTime);
          lastX = event.clientX;
          lastTime = now;
          let next = startPosition - dx / this.step;
          if (!this.loop) next = Math.max(-0.3, Math.min(this.slides.length - 0.7, next));
          this.position = next;
          this.render();
        });
        window.addEventListener('pointerup', () => {
          if (!dragging) return;
          dragging = false;
          // Snap to the nearest slide, nudged one slide in the direction of a quick flick.
          let target = Math.round(this.position);
          if (Math.abs(velocity) > 0.3 && target === Math.round(startPosition)) target += velocity < 0 ? 1 : -1;
          if (!this.loop) target = Math.max(0, Math.min(this.slides.length - 1, target));
          this.animateTo(target);
        });
        // Swallow the click that ends a drag so links are not followed.
        this.track.addEventListener(
          'click',
          (event) => {
            if (moved) {
              event.preventDefault();
              event.stopPropagation();
              moved = false;
            }
          },
          true,
        );
        this.track.addEventListener('dragstart', (event) => event.preventDefault());
      }
    },
  );
}
